import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { RefreshCw, Loader2, Mail, Send, Search, Eye, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  sent: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
};

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const AdminCommunicationsLog = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [distinctTemplates, setDistinctTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);

  // Filter state
  const [filterTemplate, setFilterTemplate] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRecipient, setFilterRecipient] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');
  const [skip, setSkip] = useState(0);
  const limit = 50;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: String(skip), limit: String(limit) });
      if (filterTemplate && filterTemplate !== 'all') params.set('template', filterTemplate);
      if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
      if (filterRecipient.trim()) params.set('recipient', filterRecipient.trim());
      if (filterSearch.trim()) params.set('search', filterSearch.trim());
      if (filterStart) params.set('start_date', filterStart);
      if (filterEnd) params.set('end_date', filterEnd);

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/communications?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
      setDistinctTemplates(data.distinct_templates || []);
    } catch (e) {
      toast.error('Failed to load communications');
    } finally {
      setLoading(false);
    }
  }, [filterTemplate, filterStatus, filterRecipient, filterSearch, filterStart, filterEnd, skip]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleResend = async (item) => {
    if (!window.confirm(
      `Re-send "${item.subject}" to ${item.user_email}?\n\nIf there's a saved override for this template, the resend will use the LATEST version.`
    )) return;
    setResendingId(item.id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/communications/${item.id}/resend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.detail || 'Resend failed');
      toast.success(`Email resent to ${item.user_email}`);
      fetchLogs();
    } catch (e) {
      toast.error(e.message || 'Resend failed');
    } finally {
      setResendingId(null);
    }
  };

  const clearFilters = () => {
    setFilterTemplate('all');
    setFilterStatus('all');
    setFilterRecipient('');
    setFilterSearch('');
    setFilterStart('');
    setFilterEnd('');
    setSkip(0);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card className="card-base">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Communications Log
              <Badge variant="outline">{total}</Badge>
            </CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={clearFilters} data-testid="comms-clear-filters">
                <X className="h-4 w-4 mr-1" /> Clear
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setSkip(0); fetchLogs(); }} disabled={loading} data-testid="comms-refresh">
                <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
            <div className="lg:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search subject / preview…"
                value={filterSearch}
                onChange={(e) => { setSkip(0); setFilterSearch(e.target.value); }}
                className="pl-9"
                data-testid="comms-search"
              />
            </div>
            <Input
              placeholder="Recipient email"
              value={filterRecipient}
              onChange={(e) => { setSkip(0); setFilterRecipient(e.target.value); }}
              data-testid="comms-recipient"
            />
            <Select value={filterTemplate} onValueChange={(v) => { setSkip(0); setFilterTemplate(v); }}>
              <SelectTrigger data-testid="comms-template-filter"><SelectValue placeholder="All templates" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All templates</SelectItem>
                {distinctTemplates.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={(v) => { setSkip(0); setFilterStatus(v); }}>
              <SelectTrigger data-testid="comms-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <Input
                type="date"
                value={filterStart}
                onChange={(e) => { setSkip(0); setFilterStart(e.target.value); }}
                className="text-xs"
                data-testid="comms-start-date"
              />
              <span className="text-xs text-gray-400">→</span>
              <Input
                type="date"
                value={filterEnd}
                onChange={(e) => { setSkip(0); setFilterEnd(e.target.value); }}
                className="text-xs"
                data-testid="comms-end-date"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="card-base">
        <CardContent className="p-0">
          {loading && items.length === 0 ? (
            <div className="p-10 text-center text-gray-400 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-gray-400">
              No communications found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="px-4 py-3">Sent</th>
                    <th className="px-4 py-3">Recipient</th>
                    <th className="px-4 py-3">Template</th>
                    <th className="px-4 py-3">Subject</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50/60">
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{fmt(item.created_at)}</td>
                      <td className="px-4 py-3 text-gray-700">{item.user_email}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs font-mono">
                          {item.type || '—'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-gray-800 max-w-md truncate" title={item.subject}>
                        {item.subject}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`text-xs ${STATUS_BADGE[item.status] || 'bg-gray-100 text-gray-700'}`}>
                          {item.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => setViewingItem(item)}
                          data-testid={`comms-view-${item.id}`}
                          title="View full preview"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => handleResend(item)}
                          disabled={resendingId === item.id || item.channel !== 'email'}
                          data-testid={`comms-resend-${item.id}`}
                          title="Resend this email"
                        >
                          {resendingId === item.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {total > limit && (
            <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500">
              <span>Showing {skip + 1}–{Math.min(skip + limit, total)} of {total}</span>
              <div className="flex gap-2">
                <Button
                  variant="outline" size="sm"
                  onClick={() => setSkip(Math.max(0, skip - limit))}
                  disabled={skip === 0}
                  data-testid="comms-prev-page"
                >
                  Previous
                </Button>
                <Button
                  variant="outline" size="sm"
                  onClick={() => setSkip(skip + limit)}
                  disabled={skip + limit >= total}
                  data-testid="comms-next-page"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={!!viewingItem} onOpenChange={(open) => !open && setViewingItem(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg">{viewingItem?.subject}</DialogTitle>
          </DialogHeader>
          {viewingItem && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-gray-500">To: </span><span className="text-gray-800">{viewingItem.user_email}</span></div>
                <div><span className="text-gray-500">Sent: </span><span className="text-gray-800">{fmt(viewingItem.created_at)}</span></div>
                <div><span className="text-gray-500">Template: </span><Badge variant="outline" className="font-mono">{viewingItem.type}</Badge></div>
                <div><span className="text-gray-500">Status: </span><Badge className={STATUS_BADGE[viewingItem.status]}>{viewingItem.status}</Badge></div>
              </div>
              <div className="border-t pt-3">
                <div className="text-xs text-gray-500 mb-1">Preview</div>
                <div className="bg-gray-50 rounded-md p-3 text-gray-700 whitespace-pre-wrap text-xs font-mono">
                  {viewingItem.preview || '(no preview text captured)'}
                </div>
              </div>
              {viewingItem.metadata && Object.keys(viewingItem.metadata).length > 0 && (
                <div className="border-t pt-3">
                  <div className="text-xs text-gray-500 mb-1">Metadata</div>
                  <pre className="bg-gray-50 rounded-md p-3 text-xs text-gray-700 overflow-x-auto">
                    {JSON.stringify(viewingItem.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCommunicationsLog;
